import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users, Calendar, TrendingUp, Award, Target, Activity, ArrowRight } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, StatCard, Card } from '../components/SharedComponents';

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    teams: [],
    players: { topScorers: [], topWicketTakers: [] },
    seasons: [],
    standings: null,
    teamStats: null,
    recentMatches: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        battingResponse,
        bowlingResponse,
        standingsResponse,
        teamStatsWins,
        teamStatsRuns,
        teamStatistics,
        seasonStatistics,
        matchesResponse
      ] = await Promise.all([
        apiService.getBattingStats('most_runs').catch(() => ({ data: { stats: [] } })),
        apiService.getBowlingStats('top_wicket_takers').catch(() => ({ data: { stats: [] } })),
        apiService.getStandings().catch(() => null),
        apiService.getTeamStats('match_win').catch(() => ({ data: { stats: [] } })),
        apiService.getTeamStats('total_runs').catch(() => ({ data: { stats: [] } })),
        apiService.getTeamStatistics().catch(() => ({ data: [] })),
        apiService.getSeasonStatistics().catch(() => ({ data: [] })),
        apiService.getAllMatchFiles().catch(() => ({ data: [] }))
      ]);

      let topScorers = [];
      if (battingResponse?.data?.stats) {
        topScorers = battingResponse.data.stats.slice(0, 15);
      }

      let topWicketTakers = [];
      if (bowlingResponse?.data?.stats) {
        topWicketTakers = bowlingResponse.data.stats.slice(0, 15);
      }

      let standings = null;
      if (standingsResponse?.data?.standings?.[0]?.standings) {
        standings = standingsResponse.data.standings[0].standings;
      } else if (standingsResponse?.data?.standings) {
        standings = standingsResponse.data.standings;
      }

      let teamWins = [];
      if (teamStatsWins?.data?.stats) {
        teamWins = teamStatsWins.data.stats;
      }

      let recentMatches = [];
      if (matchesResponse?.data) {
        recentMatches = matchesResponse.data.slice(0, 6);
      }

      setStats({
        teams: teamStatistics.data || [],
        players: { topScorers, topWicketTakers },
        seasons: seasonStatistics.data || [],
        standings,
        teamStats: {
          wins: teamWins,
          runs: teamStatsRuns?.data?.stats || []
        },
        recentMatches
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading message="Loading dashboard..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchDashboardData} />;

  const totalMatches = stats.seasons.reduce((sum, season) => sum + parseInt(season.total_matches || 0), 0);
  const totalTeams = stats.standings?.length || stats.teams.length || 10;

  const topScorer = stats.players.topScorers[0];
  const topScorerRuns = topScorer?.runs || topScorer?.total_runs || 0;
  const topScorerName = topScorer?.player?.title || topScorer?.name || 'Loading...';

  const topWicketTaker = stats.players.topWicketTakers[0];
  const topWicketTakerWickets = topWicketTaker?.wickets || topWicketTaker?.total_wickets || 0;
  const topWicketTakerName = topWicketTaker?.player?.title || topWicketTaker?.name || 'Loading...';

  const topScorersData = stats.players.topScorers.slice(0, 8).map(player => {
    const playerName = player.player?.title || player.name || 'Unknown';
    return {
      name: playerName.split(' ').slice(-1)[0],
      runs: parseInt(player.total_runs || player.runs || 0),
      fullName: playerName,
      average: parseFloat(player.average || player.Maverage || 0).toFixed(1),
      strikeRate: parseFloat(player.strike || player.strike_rate || 0).toFixed(1)
    };
  });

  const topWicketTakersData = stats.players.topWicketTakers.slice(0, 8).map(player => {
    const playerName = player.player?.title || player.name || 'Unknown';
    return {
      name: playerName.split(' ').slice(-1)[0],
      wickets: parseInt(player.total_wickets || player.wickets || 0),
      fullName: playerName,
      economy: parseFloat(player.economy || player.econ || 0).toFixed(2),
      average: parseFloat(player.average || player.bowl_average || 0).toFixed(1)
    };
  });

  const teamWinData = (stats.teamStats?.wins || []).slice(0, 8).map(team => ({
    name: team.team?.abbr || team.team?.title || 'Unknown',
    wins: parseInt(team.value || team.win || 0),
    fullName: team.team?.title || 'Unknown'
  }));

  const standingsData = (stats.standings || []).map((team, index) => ({
    position: index + 1,
    team: team.team?.abbr || team.team?.title || 'Unknown',
    fullName: team.team?.title || 'Unknown',
    played: parseInt(team.played || 0),
    won: parseInt(team.win || 0),
    lost: parseInt(team.loss || 0),
    points: parseInt(team.points || 0),
    nrr: parseFloat(team.netrr || 0).toFixed(3)
  }));

  const COLORS = ['#e85d04', '#6366f1', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899'];

  const CustomTooltip = ({ active, payload, type }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;

    return (
      <div style={{
        backgroundColor: 'white',
        padding: '14px 18px',
        borderRadius: '12px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
        border: '1px solid #f5f5f4'
      }}>
        <p style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: '600',
          margin: '0 0 8px 0',
          color: '#1c1917',
          fontSize: '14px'
        }}>
          {data.fullName}
        </p>
        {type === 'runs' ? (
          <>
            <p style={{ color: '#e85d04', margin: '4px 0', fontSize: '13px' }}>
              Runs: <strong>{data.runs}</strong>
            </p>
            <p style={{ color: '#57534e', margin: '4px 0', fontSize: '12px' }}>
              Avg: {data.average} | SR: {data.strikeRate}
            </p>
          </>
        ) : type === 'wickets' ? (
          <>
            <p style={{ color: '#22c55e', margin: '4px 0', fontSize: '13px' }}>
              Wickets: <strong>{data.wickets}</strong>
            </p>
            <p style={{ color: '#57534e', margin: '4px 0', fontSize: '12px' }}>
              Econ: {data.economy} | Avg: {data.average}
            </p>
          </>
        ) : (
          <p style={{ color: '#6366f1', margin: '4px 0', fontSize: '13px' }}>
            Wins: <strong>{payload[0].value}</strong>
          </p>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header */}
      <div>
        <h1 style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '32px',
          fontWeight: '700',
          color: '#1c1917',
          margin: 0,
          letterSpacing: '-0.5px'
        }}>
          IPL Dashboard
        </h1>
        <p style={{
          color: '#57534e',
          marginTop: '6px',
          fontSize: '15px'
        }}>
          Indian Premier League 2022 Statistics
        </p>
      </div>

      {/* KPI Cards */}
      <div
        className="dashboard-stats"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px'
        }}
      >
        <StatCard title="Total Teams" value={totalTeams} subtitle="Competing teams" icon={Users} />
        <StatCard title="Total Matches" value={totalMatches} subtitle="Across all seasons" icon={Calendar} />
        <StatCard title="Top Run Scorer" value={topScorerRuns} subtitle={topScorerName} icon={TrendingUp} />
        <StatCard title="Top Wicket Taker" value={topWicketTakerWickets} subtitle={topWicketTakerName} icon={Award} />
      </div>

      {/* Points Table */}
      {standingsData.length > 0 && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              padding: '10px',
              background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
              borderRadius: '10px'
            }}>
              <Trophy size={20} color="white" />
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '20px',
              fontWeight: '600',
              margin: 0,
              color: '#1c1917'
            }}>
              Points Table
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>#</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Team</th>
                  <th style={tableHeaderStyle}>P</th>
                  <th style={tableHeaderStyle}>W</th>
                  <th style={tableHeaderStyle}>L</th>
                  <th style={tableHeaderStyle}>Pts</th>
                  <th style={tableHeaderStyle}>NRR</th>
                </tr>
              </thead>
              <tbody>
                {standingsData.map((team, index) => (
                  <tr
                    key={index}
                    style={{ transition: 'background-color 0.15s' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafaf9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={tableCellStyle}>
                      <div style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '8px',
                        backgroundColor: index < 4 ? '#dcfce7' : '#f5f5f4',
                        color: index < 4 ? '#22c55e' : '#57534e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '13px',
                        margin: '0 auto'
                      }}>
                        {team.position}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'left' }}>
                      <div>
                        <div style={{ color: '#1c1917', fontWeight: '600', fontSize: '14px' }}>{team.team}</div>
                        <div style={{ color: '#a8a29e', fontSize: '12px', marginTop: '1px' }}>{team.fullName}</div>
                      </div>
                    </td>
                    <td style={tableCellStyle}>{team.played}</td>
                    <td style={{ ...tableCellStyle, color: '#22c55e', fontWeight: '600' }}>{team.won}</td>
                    <td style={{ ...tableCellStyle, color: '#ef4444', fontWeight: '600' }}>{team.lost}</td>
                    <td style={{ ...tableCellStyle, fontWeight: '700', color: '#e85d04' }}>{team.points}</td>
                    <td style={{
                      ...tableCellStyle,
                      color: parseFloat(team.nrr) >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '500'
                    }}>
                      {parseFloat(team.nrr) >= 0 ? '+' : ''}{team.nrr}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
        gap: '20px'
      }}>
        {/* Top Run Scorers */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              padding: '10px',
              background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
              borderRadius: '10px'
            }}>
              <Target size={20} color="white" />
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '18px',
              fontWeight: '600',
              margin: 0,
              color: '#1c1917'
            }}>
              Top Run Scorers
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topScorersData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: '#57534e', fontSize: 12 }}
                axisLine={{ stroke: '#e7e5e4' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#57534e', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip type="runs" />} />
              <Bar dataKey="runs" fill="#e85d04" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top Wicket Takers */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              padding: '10px',
              background: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)',
              borderRadius: '10px'
            }}>
              <Activity size={20} color="white" />
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '18px',
              fontWeight: '600',
              margin: 0,
              color: '#1c1917'
            }}>
              Top Wicket Takers
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topWicketTakersData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: '#57534e', fontSize: 12 }}
                axisLine={{ stroke: '#e7e5e4' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#57534e', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip type="wickets" />} />
              <Bar dataKey="wickets" fill="#22c55e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Team Wins */}
      {teamWinData.length > 0 && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              padding: '10px',
              background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
              borderRadius: '10px'
            }}>
              <Trophy size={20} color="white" />
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '18px',
              fontWeight: '600',
              margin: 0,
              color: '#1c1917'
            }}>
              Team Wins Distribution
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={teamWinData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, wins }) => `${name}: ${wins}`}
                outerRadius={110}
                fill="#8884d8"
                dataKey="wins"
                stroke="white"
                strokeWidth={2}
              >
                {teamWinData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip type="wins" />} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Recent Matches */}
      {stats.recentMatches.length > 0 && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                padding: '10px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                borderRadius: '10px'
              }}>
                <Calendar size={20} color="white" />
              </div>
              <h2 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '18px',
                fontWeight: '600',
                margin: 0,
                color: '#1c1917'
              }}>
                Recent Matches
              </h2>
            </div>
            <button
              onClick={() => navigate('/matches')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(232, 93, 4, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              View All
              <ArrowRight size={14} />
            </button>
          </div>
          <div className="match-card-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: '14px'
          }}>
            {stats.recentMatches.map((match, index) => (
              <div
                key={match.matchId || index}
                onClick={() => navigate(`/matches/${match.filename}`)}
                style={{
                  padding: '16px',
                  backgroundColor: '#fafaf9',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: '1px solid #f5f5f4'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f4';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafaf9';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#a8a29e', marginBottom: '4px', fontWeight: '500' }}>
                    {match.date ? new Date(match.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 'N/A'}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1c1917' }}>
                    {match.shortTitle || match.title}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {match.teams?.teamA?.logo_url && (
                        <img
                          src={match.teams.teamA.logo_url}
                          alt={match.teams.teamA.short_name}
                          style={{ width: '22px', height: '22px', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span style={{ fontWeight: '500', fontSize: '13px', color: '#1c1917' }}>
                        {match.teams?.teamA?.short_name || 'Team A'}
                      </span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1c1917' }}>
                      {match.teams?.teamA?.scores || '-'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {match.teams?.teamB?.logo_url && (
                        <img
                          src={match.teams.teamB.logo_url}
                          alt={match.teams.teamB.short_name}
                          style={{ width: '22px', height: '22px', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span style={{ fontWeight: '500', fontSize: '13px', color: '#1c1917' }}>
                        {match.teams?.teamB?.short_name || 'Team B'}
                      </span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1c1917' }}>
                      {match.teams?.teamB?.scores || '-'}
                    </span>
                  </div>
                </div>
                <div style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid #e7e5e4',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontSize: '12px', color: '#e85d04', fontWeight: '600' }}>
                    View Details
                  </span>
                  <ArrowRight size={12} color="#e85d04" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

const tableHeaderStyle = {
  padding: '12px 14px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '12px',
  color: '#57534e',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid #e7e5e4',
  backgroundColor: '#fafaf9'
};

const tableCellStyle = {
  padding: '14px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#1c1917',
  borderBottom: '1px solid #f5f5f4'
};

export default Dashboard;
